import { Dialog, DialogContent } from '@civilgrid/ui/components';
import { cn } from '@civilgrid/ui/utils';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Link } from '@tanstack/react-router';
import { isFunction } from 'lodash';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import Icon from '~/components2/Icon';

interface ItemBase {
  type: string;
  label: ReactNode;
  isDisabled?: boolean;
  isAllowedTo?: Action;
  /** Applied to the interactive menu element (`DropdownMenu.Item` / `SubTrigger`), not the label. */
  'data-testid'?: string;
}

interface SimpleItem extends ItemBase {
  type: 'simple';
  onClick: () => void;
}

interface StaticItem extends ItemBase {
  type: 'static';
}

interface DialogItem extends ItemBase {
  type: 'dialog';
  dialogTitle: string;
  /** Rendered inside the ui `DialogContent`, so it supplies its own `DialogBody` and `DialogFooter`. */
  dialogContent: (closeDialog: () => void) => ReactNode;
  /** Width classes for the dialog content. */
  dialogClassName?: string;
  onItemSelect?: () => void;
}

interface LinkItem extends ItemBase {
  type: 'link';
  url: string;
}

interface SubMenu extends ItemBase {
  type: 'subMenu';
  label: string;
  items: Item[];
}

interface Separator {
  type: 'separator';
}

export type Item = SubMenu | SimpleItem | StaticItem | DialogItem | LinkItem | Separator;

/** Finds the dialog item at `path`, an index per menu level, in the current `items`. */
function findDialogItem(items: Item[], path: number[]): DialogItem | null {
  const [index, ...rest] = path;
  const item = items[index];
  if (!item) return null;
  if (rest.length === 0) return item.type === 'dialog' ? item : null;
  return item.type === 'subMenu' ? findDialogItem(item.items, rest) : null;
}

interface Props {
  triggerElement: JSX.Element | ((isOpen: boolean) => JSX.Element);
  items: Item[];
  align?: DropdownMenu.DropdownMenuContentProps['align'];
  sideOffset?: number;
  alignOffset?: number;
  side?: 'top' | 'right' | 'bottom' | 'left';
  setIsOpen?: (open: boolean) => void;
  onItemSelect?: () => void;
  container?: HTMLDivElement | null;
  disabled?: boolean;
  /** Applied to the interactive trigger. */
  'data-testid'?: string;
  /** Applied to the portalled menu panel, which only exists while the menu is open. */
  contentTestId?: string;
}

export const Dropdown = ({
  triggerElement,
  items,
  align = 'start',
  sideOffset = 4,
  alignOffset = 0,
  side = 'bottom',
  setIsOpen,
  container,
  disabled = false,
  'data-testid': dataTestId,
  contentTestId,
}: Props) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeDialogPath, setActiveDialogPath] = useState<number[] | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Resolved from the current `items` each render so the open dialog sees fresh props and state.
  const activeDialog = activeDialogPath ? findDialogItem(items, activeDialogPath) : null;
  const isOpen = dropdownOpen || activeDialog !== null;

  // Reports open for as long as the menu or a dialog it opened is showing.
  useEffect(() => {
    setIsOpen?.(isOpen);
  }, [isOpen, setIsOpen]);

  const closeDialog = () => setActiveDialogPath(null);

  useEffect(() => {
    if (activeDialogPath && !activeDialog) setActiveDialogPath(null);
  }, [activeDialogPath, activeDialog]);

  const menuItems = useMemo(() => {
    if (!dropdownOpen) return;
    const renderItems = (items: Item[], parentPath: number[] = []) => {
      return items.map((item, i) => {
        if (item.type === 'simple') {
          return (
            <Can
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy
              key={i}
              action={item.isAllowedTo}
            >
              <DropdownMenu.Item
                // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                key={i}
                className={cn(
                  'relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-xs outline-none select-none',
                  'text-primary-dark hover:bg-grey-10 focus:bg-grey-10 data-[highlighted]:bg-grey-10',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                  '[&_svg]:pointer-events-none [&_svg]:shrink-0',
                )}
                onClick={item.onClick}
                disabled={item.isDisabled}
                data-testid={item['data-testid']}
              >
                {item.label}
              </DropdownMenu.Item>
            </Can>
          );
        } else if (item.type === 'static') {
          return (
            <DropdownMenu.Item
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy
              key={i}
              className={cn(
                'relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-xs outline-none select-none',
                'text-primary-dark hover:bg-grey-10 focus:bg-grey-10 data-[highlighted]:bg-grey-10',
                'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                '[&_svg]:pointer-events-none [&_svg]:shrink-0',
                'pointer-events-none',
              )}
              data-testid={item['data-testid']}
            >
              {item.label}
            </DropdownMenu.Item>
          );
        } else if (item.type === 'dialog') {
          return (
            <Can
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy
              key={i}
              action={item.isAllowedTo}
            >
              <DropdownMenu.Item
                className={cn(
                  'relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-xs outline-none select-none',
                  'text-primary-dark hover:bg-grey-10 focus:bg-grey-10 data-[highlighted]:bg-grey-10',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                  '[&_svg]:pointer-events-none [&_svg]:shrink-0',
                )}
                onSelect={() => {
                  item.onItemSelect?.();
                  setActiveDialogPath([...parentPath, i]);
                }}
                disabled={item.isDisabled}
                aria-haspopup="dialog"
                data-testid={item['data-testid']}
              >
                {item.label}
              </DropdownMenu.Item>
            </Can>
          );
        } else if (item.type === 'subMenu') {
          return (
            <DropdownMenu.Sub
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy
              key={i}
            >
              <DropdownMenu.SubTrigger
                className={cn(
                  'flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-xs outline-none select-none',
                  'text-primary-dark hover:bg-grey-10 focus:bg-grey-10 data-[highlighted]:bg-grey-10 data-[state=open]:bg-grey-10',
                  '[&_svg]:pointer-events-none [&_svg]:shrink-0',
                )}
                data-testid={item['data-testid']}
              >
                {item.label}
                <Icon icon="chevronRight" size={14} className="ml-auto text-grey-50" />
              </DropdownMenu.SubTrigger>
              <DropdownMenu.Portal>
                <DropdownMenu.SubContent
                  // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                  key={i}
                  sideOffset={2}
                  className={cn(
                    'bg-white text-primary border border-grey-30 rounded-sm p-1 shadow-md',
                    'data-[state=open]:animate-in data-[state=closed]:animate-out',
                    'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                    'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                    'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
                    'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                    'z-[150] min-w-32 origin-[var(--radix-dropdown-menu-content-transform-origin)] overflow-hidden',
                  )}
                >
                  {renderItems(item.items, [...parentPath, i])}
                </DropdownMenu.SubContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Sub>
          );
        } else if (item.type === 'link') {
          return (
            <Can
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy
              key={i}
              action={item.isAllowedTo}
            >
              <Link to={item.url}>
                <DropdownMenu.Item
                  className={cn(
                    'relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-xs outline-none select-none',
                    'text-primary-dark hover:bg-grey-10 focus:bg-grey-10 data-[highlighted]:bg-grey-10',
                    'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
                  )}
                  data-testid={item['data-testid']}
                >
                  {item.label}
                </DropdownMenu.Item>
              </Link>
            </Can>
          );
        } else if (item.type === 'separator') {
          return (
            <DropdownMenu.Separator
              className="bg-grey-30 -mx-1 my-1 h-px"
              // biome-ignore lint/suspicious/noArrayIndexKey: legacy
              key={`${item}-${i}`}
            />
          );
        } else {
          return null;
        }
      });
    };

    return renderItems(items);
  }, [items, dropdownOpen]);

  return (
    <>
      <DropdownMenu.Root open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenu.Trigger
          ref={triggerRef}
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
          data-testid={dataTestId}
          asChild
        >
          {isFunction(triggerElement) ? triggerElement(dropdownOpen) : triggerElement}
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal container={container ?? undefined}>
          <DropdownMenu.Content
            className={cn(
              'bg-white text-primary border border-grey-30 rounded-sm p-1 shadow-md',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
              'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
              'z-[150] max-h-(--radix-dropdown-menu-content-available-height) min-w-32',
              'origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto',
            )}
            align={align}
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            onCloseAutoFocus={(e) => e.preventDefault()}
            side={side}
            data-testid={contentTestId}
          >
            {menuItems}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      <Dialog
        open={activeDialog !== null}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        {activeDialog && (
          <DialogContent
            title={activeDialog.dialogTitle}
            className={cn('z-[110] w-auto min-w-min max-w-[75vw]', activeDialog.dialogClassName)}
            overlayClassName="z-[100]"
            data-testid={activeDialog['data-testid'] ? `${activeDialog['data-testid']}-dialog` : undefined}
            // The menu item that opened the dialog is gone by close, so focus returns to the trigger instead.
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              triggerRef.current?.focus();
            }}
          >
            {activeDialog.dialogContent(closeDialog)}
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};
